import api from './api.service';

/** Heavy report queries can exceed the default API timeout (30s) on large datasets. */
const FINANCE_REPORT_TIMEOUT_MS = 300_000;

export interface TrialBalanceRow {
  account_id: string;
  account_code: string;
  account_name: string;
  account_type: string;
  opening_debit: number;
  opening_credit: number;
  period_debit: number;
  period_credit: number;
  closing_debit: number;
  closing_credit: number;
}

export interface TrialBalanceReport {
  company_id: string;
  as_of_date: string;
  from_date?: string;
  currency: string;
  rows: TrialBalanceRow[];
  total_opening_debit: number;
  total_opening_credit: number;
  total_period_debit: number;
  total_period_credit: number;
  total_closing_debit: number;
  total_closing_credit: number;
  is_balanced: boolean;
}

export interface PLSection {
  section_name: string;
  accounts: Array<{ account_code: string; account_name: string; amount: number }>;
  subtotal: number;
}

export interface ProfitLossReport {
  company_id: string;
  from_date: string;
  to_date: string;
  currency: string;
  revenue: PLSection;
  cost_of_goods_sold: PLSection;
  gross_profit: number;
  gross_profit_margin: number;
  operating_expenses: PLSection;
  operating_profit: number;
  operating_profit_margin: number;
  other_income: PLSection;
  other_expenses: PLSection;
  profit_before_tax: number;
  tax_expense: number;
  net_profit: number;
  net_profit_margin: number;
}

export interface BSSection {
  section_name: string;
  accounts: Array<{ account_code: string; account_name: string; amount: number }>;
  subtotal: number;
}

export interface BalanceSheetReport {
  company_id: string;
  as_of_date: string;
  currency: string;
  current_assets: BSSection;
  fixed_assets: BSSection;
  other_assets: BSSection;
  total_assets: number;
  current_liabilities: BSSection;
  long_term_liabilities: BSSection;
  total_liabilities: number;
  equity: BSSection;
  retained_earnings: number;
  total_equity: number;
  total_liabilities_and_equity: number;
  is_balanced: boolean;
}

export interface CashFlowLineItem {
  voucher_id: string;
  voucher_number: string;
  voucher_date: string;
  voucher_type: string;
  reference_number?: string;
  reference_type?: string;
  reference_id?: string;
  account_code: string;
  account_name: string;
  description?: string;
  amount: number;
  section: 'Operating' | 'Investing' | 'Financing' | 'InternalTransfer';
}

export interface CashFlowSection {
  section_name: string;
  section_type: string;
  lines: CashFlowLineItem[];
  subtotal: number;
}

export interface CashFlowReport {
  company_id: string;
  from_date: string;
  to_date: string;
  currency: string;
  operating: CashFlowSection;
  investing: CashFlowSection;
  financing: CashFlowSection;
  internal_transfers: CashFlowSection;
  net_cash_from_operating: number;
  net_cash_from_investing: number;
  net_cash_from_financing: number;
  net_increase_decrease_cash: number;
  opening_cash_and_equivalents: number;
  closing_cash_and_equivalents: number;
  reconciliation_ok: boolean;
}

export async function getTrialBalance(params: {
  as_of_date: string;
  from_date?: string;
}): Promise<TrialBalanceReport> {
  const response = await api.get<{ success: boolean; data: TrialBalanceReport }>(
    '/finance/reports/trial-balance',
    { params, timeout: FINANCE_REPORT_TIMEOUT_MS }
  );
  const data = (response as any)?.data ?? response;
  return data?.data ?? data;
}

export async function getProfitLoss(params: {
  from_date: string;
  to_date: string;
}): Promise<ProfitLossReport> {
  const response = await api.get<{ success: boolean; data: ProfitLossReport }>(
    '/finance/reports/profit-loss',
    { params, timeout: FINANCE_REPORT_TIMEOUT_MS }
  );
  const data = (response as any)?.data ?? response;
  return data?.data ?? data;
}

export type ProjectPnlSection = 'revenue' | 'direct_cost' | 'overhead' | 'other_income';

export interface ProjectReportHeader {
  project_id: string;
  project_number: string | null;
  project_name: string | null;
  customer_name: string | null;
  project_status: string | null;
  contract_value: number | null;
  start_date: string | null;
}

export interface ProjectPnlTotals {
  revenue: number;
  direct_cost: number;
  gross_profit: number;
  gross_margin_pct: number | null;
  overhead: number;
  other_income: number;
  net_profit: number;
  net_margin_pct: number | null;
}

export interface ProjectLedgerLine {
  line_id: string;
  voucher_id: string;
  voucher_number: string;
  voucher_date: string;
  voucher_type: string;
  reference_number?: string;
  reference_type?: string;
  reference_id?: string;
  description?: string;
  account_id: string;
  account_code: string;
  account_name: string;
  account_type: string;
  pnl_section: ProjectPnlSection | null;
  debit_amount: number;
  credit_amount: number;
  attribution: string | null;
}

export interface ProjectLedgerAccount {
  account_id: string;
  account_code: string;
  account_name: string;
  account_type: string;
  account_category: string | null;
  pnl_section: ProjectPnlSection | null;
  opening_balance: number;
  debit: number;
  credit: number;
  closing_balance: number;
  line_count: number;
}

export interface ProjectLedgerReport {
  project: ProjectReportHeader;
  currency: string;
  from_date: string;
  to_date: string;
  scope: 'all' | 'pl' | 'bs';
  accounts: ProjectLedgerAccount[];
  lines: ProjectLedgerLine[];
  lines_truncated: boolean;
  total_debit: number;
  total_credit: number;
  period_pnl: ProjectPnlTotals;
}

export interface ProjectProfitLossReport {
  project: ProjectReportHeader;
  currency: string;
  from_date: string;
  to_date: string;
  sections: Array<{
    key: ProjectPnlSection;
    label: string;
    accounts: Array<{
      account_id: string;
      account_code: string;
      account_name: string;
      account_category: string | null;
      period_amount: number;
      to_date_amount: number;
    }>;
    period_total: number;
    to_date_total: number;
  }>;
  period: ProjectPnlTotals;
  project_to_date: ProjectPnlTotals;
}

export interface ProjectProfitLossSummaryRow extends ProjectPnlTotals {
  project_id: string;
  project_number: string | null;
  project_name: string | null;
  customer_name: string | null;
  project_status: string | null;
}

export interface ProjectProfitLossSummary {
  currency: string;
  from_date: string;
  to_date: string;
  rows: ProjectProfitLossSummaryRow[];
  totals: ProjectPnlTotals;
}

export async function getProjectLedger(params: {
  project_id: string;
  from_date: string;
  to_date: string;
  scope?: 'all' | 'pl' | 'bs';
  branch_id?: string;
}): Promise<ProjectLedgerReport> {
  const response = await api.get('/finance/reports/project-ledger', { params, timeout: FINANCE_REPORT_TIMEOUT_MS });
  const data = (response as any)?.data ?? response;
  return data?.data ?? data;
}

export async function getProjectProfitLoss(params: {
  project_id: string;
  from_date: string;
  to_date: string;
  branch_id?: string;
}): Promise<ProjectProfitLossReport> {
  const response = await api.get('/finance/reports/project-profit-loss', { params, timeout: FINANCE_REPORT_TIMEOUT_MS });
  const data = (response as any)?.data ?? response;
  return data?.data ?? data;
}

export async function getProjectProfitLossSummary(params: {
  from_date: string;
  to_date: string;
  branch_id?: string;
}): Promise<ProjectProfitLossSummary> {
  const response = await api.get('/finance/reports/project-profit-loss', { params, timeout: FINANCE_REPORT_TIMEOUT_MS });
  const data = (response as any)?.data ?? response;
  return data?.data ?? data;
}

export interface MonthlyPnlMonth {
  key: string;
  label: string;
  from_date: string;
  to_date: string;
}

export interface MonthlyPnlAccountRow {
  account_id: string;
  account_code: string;
  account_name: string;
  account_category: string | null;
  amounts: number[];
  total: number;
  prior_amounts: number[] | null;
  prior_total: number | null;
}

export interface MonthlyPnlSectionBlock {
  key: ProjectPnlSection;
  label: string;
  accounts: MonthlyPnlAccountRow[];
  amounts: number[];
  total: number;
  prior_amounts: number[] | null;
  prior_total: number | null;
}

export interface MonthlyPnlSummaryMonth {
  month: string;
  current: ProjectPnlTotals;
  prior: ProjectPnlTotals | null;
  ytd: ProjectPnlTotals;
}

export interface MonthlyProfitLossReport {
  currency: string;
  from_month: string;
  to_month: string;
  fiscal_year_start_month: number;
  filters: { department_id: string | null; branch_id: string | null; compare: boolean };
  months: MonthlyPnlMonth[];
  sections: MonthlyPnlSectionBlock[];
  summary: MonthlyPnlSummaryMonth[];
  totals: ProjectPnlTotals;
  prior_totals: ProjectPnlTotals | null;
}

export async function getMonthlyProfitLoss(params: {
  from_month?: string;
  to_month?: string;
  department_id?: string;
  branch_id?: string;
  compare?: boolean;
}): Promise<MonthlyProfitLossReport> {
  const response = await api.get('/finance/reports/monthly-profit-loss', {
    params: { ...params, compare: params.compare ? 'true' : undefined },
    timeout: FINANCE_REPORT_TIMEOUT_MS,
  });
  const data = (response as any)?.data ?? response;
  return data?.data ?? data;
}

export interface OperationalProfitMonth {
  month: string;
  delivered_value: number;
  invoiced_value: number;
  delivered_cost: number;
  stock_cost: number;
  estimated_cost: number;
  gross_profit: number;
  gross_margin_pct: number | null;
  uncosted_lines: number;
  uncosted_value: number;
  overhead: number;
  other_income: number;
  net_profit: number;
  net_margin_pct: number | null;
  goods_received: number;
  non_po_invoices: number;
  actual_spend: number;
  finance_net_profit: number;
}

export type OperationalProfitTotals = Omit<OperationalProfitMonth, 'month'>;

export interface UncostedDeliveryLine {
  delivery_note_id: string;
  delivery_note_number: string;
  delivered_on: string;
  sales_order_id: string;
  sales_order_number: string;
  customer_name: string | null;
  item_code: string | null;
  description: string | null;
  quantity: number;
  value: number;
}

export interface OperationalProfitReport {
  currency: string;
  from_month: string;
  to_month: string;
  months: MonthlyPnlMonth[];
  rows: OperationalProfitMonth[];
  totals: OperationalProfitTotals;
  uncosted: UncostedDeliveryLine[];
  uncosted_truncated: boolean;
}

export async function getOperationalProfit(params: {
  from_month?: string;
  to_month?: string;
}): Promise<OperationalProfitReport> {
  const response = await api.get('/finance/reports/operational-profit', {
    params,
    timeout: FINANCE_REPORT_TIMEOUT_MS,
  });
  const data = (response as any)?.data ?? response;
  return data?.data ?? data;
}

export async function getBalanceSheet(params: { as_of_date: string }): Promise<BalanceSheetReport> {
  const response = await api.get<{ success: boolean; data: BalanceSheetReport }>(
    '/finance/reports/balance-sheet',
    { params, timeout: FINANCE_REPORT_TIMEOUT_MS }
  );
  const data = (response as any)?.data ?? response;
  return data?.data ?? data;
}

export async function getCashFlow(params: { from_date: string; to_date: string }): Promise<CashFlowReport> {
  const response = await api.get<{ success: boolean; data: CashFlowReport }>(
    '/finance/reports/cash-flow',
    { params, timeout: FINANCE_REPORT_TIMEOUT_MS }
  );
  const data = (response as any)?.data ?? response;
  return data?.data ?? data;
}

export type LedgerType = 'account' | 'supplier' | 'customer' | 'bank' | 'cash' | 'project';

export interface LedgerLine {
  voucher_id: string;
  voucher_number: string;
  voucher_date: string;
  voucher_type: string;
  reference_number?: string;
  description?: string;
  debit_amount: number;
  credit_amount: number;
  running_balance: number;
  reference_type?: string;
  reference_id?: string;
  account_code?: string;
  account_name?: string;
}

export interface LedgerReport {
  opening_balance: number;
  closing_balance: number;
  currency: string;
  lines: LedgerLine[];
  total_debit: number;
  total_credit: number;
}

export interface GetLedgerParams {
  ledger_type: LedgerType;
  account_id?: string;
  supplier_id?: string;
  customer_id?: string;
  bank_account_id?: string;
  project_id?: string;
  from_date: string;
  to_date: string;
}

export async function getLedger(params: GetLedgerParams): Promise<LedgerReport> {
  const response = await api.get<{ success: boolean; data: LedgerReport }>(
    '/finance/reports/ledger',
    { params }
  );
  const data = (response as any)?.data ?? response;
  return data?.data ?? data;
}

// Customer / Supplier Outstanding and Statement reports
export interface CustomerOutstandingSummaryRow {
  customer_id: string;
  customer_code: string;
  customer_name: string;
  total_outstanding: number;
  total_overdue: number;
  oldest_due_date: string | null;
  latest_invoice_date: string | null;
}

export interface SupplierOutstandingSummaryRow {
  supplier_id: string;
  supplier_code: string;
  supplier_name: string;
  total_outstanding: number;
  total_overdue: number;
  oldest_due_date: string | null;
  latest_bill_date: string | null;
}

export interface CustomerOutstandingDetailRow {
  invoice_id: string;
  invoice_number: string;
  invoice_date: string;
  due_date: string;
  grand_total: number;
  paid_amount: number;
  balance_due: number;
  overdue_days: number;
  due_days: number;
  description: string;
  customer_id: string;
  customer_name: string;
  project_id: string | null;
  invoice_type: string;
}

export interface SupplierOutstandingDetailRow {
  invoice_id: string;
  invoice_number: string;
  supplier_invoice_number: string;
  supplier_invoice_date: string;
  due_date: string;
  grand_total: number;
  paid_amount: number;
  balance_due: number;
  overdue_days: number;
  due_days: number;
  description: string;
  supplier_id: string;
  supplier_name: string;
  invoice_type?: string;
}

export interface StatementLine {
  voucher_id: string;
  voucher_number: string;
  voucher_date: string;
  voucher_type: string;
  reference_number?: string;
  description?: string;
  debit_amount: number;
  credit_amount: number;
  running_balance: number;
  reference_type?: string;
  reference_id?: string;
  account_code?: string;
  account_name?: string;
}

export interface StatementReport {
  opening_balance: number;
  closing_balance: number;
  currency: string;
  lines: StatementLine[];
  total_debit: number;
  total_credit: number;
}

export async function getCustomerOutstandingSummary(params?: {
  as_on_date?: string;
  customer_id?: string;
}): Promise<CustomerOutstandingSummaryRow[]> {
  const response = await api.get<{ success: boolean; data: CustomerOutstandingSummaryRow[] }>(
    '/finance/reports/customer-outstanding-summary',
    { params: params ?? {} }
  );
  const data = (response as any)?.data ?? response;
  return data?.data ?? data ?? [];
}

export async function getSupplierOutstandingSummary(params?: {
  as_on_date?: string;
  supplier_id?: string;
}): Promise<SupplierOutstandingSummaryRow[]> {
  const response = await api.get<{ success: boolean; data: SupplierOutstandingSummaryRow[] }>(
    '/finance/reports/supplier-outstanding-summary',
    { params: params ?? {} }
  );
  const data = (response as any)?.data ?? response;
  return data?.data ?? data ?? [];
}

export async function getCustomerOutstandingDetail(params?: { customer_id?: string; as_on_date?: string }): Promise<CustomerOutstandingDetailRow[]> {
  const response = await api.get<{ success: boolean; data: CustomerOutstandingDetailRow[] }>(
    '/finance/reports/customer-outstanding-detail',
    { params: params ?? {} }
  );
  const data = (response as any)?.data ?? response;
  return data?.data ?? data ?? [];
}

export async function getSupplierOutstandingDetail(params?: { supplier_id?: string; as_on_date?: string }): Promise<SupplierOutstandingDetailRow[]> {
  const response = await api.get<{ success: boolean; data: SupplierOutstandingDetailRow[] }>(
    '/finance/reports/supplier-outstanding-detail',
    { params: params ?? {} }
  );
  const data = (response as any)?.data ?? response;
  return data?.data ?? data ?? [];
}

export type CustomerStatementMode = 'activity' | 'open_items';
export type CustomerStatementEntityType = 'sales_invoice' | 'receipt_voucher' | 'customer_credit_note';
export type CustomerStatementAgeingBucket = 'not_due' | 'd1_30' | 'd31_60' | 'd61_90' | 'd91_120' | 'over_120' | 'unapplied';

export interface CustomerStatementTransaction {
  date: string;
  type: 'invoice' | 'receipt' | 'settlement_adjustment' | 'advance_adjustment' | 'credit_note';
  type_label: string;
  entity_type: CustomerStatementEntityType;
  document_id: string;
  document_number: string;
  customer_lpo: string | null;
  customer_lpo_date: string | null;
  delivery_notes: string[];
  sales_order_number: string | null;
  due_date: string | null;
  description: string;
  debit: number;
  credit: number;
  balance: number;
}

export interface CustomerStatementOpenItem {
  kind: 'invoice' | 'unapplied_receipt' | 'unapplied_credit_note';
  entity_type: CustomerStatementEntityType;
  document_id: string;
  document_number: string;
  date: string;
  due_date: string | null;
  customer_lpo: string | null;
  customer_lpo_date: string | null;
  delivery_notes: string[];
  sales_order_number: string | null;
  original_amount: number;
  settled_amount: number;
  open_amount: number;
  open_amount_base: number;
  days_overdue: number;
  bucket: CustomerStatementAgeingBucket;
}

export type CustomerStatementAgeing = Record<CustomerStatementAgeingBucket | 'total', number>;

export interface CustomerStatementSection {
  currency: string;
  is_base_currency: boolean;
  opening_balance: number;
  invoiced: number;
  received: number;
  adjustments: number;
  credit_notes: number;
  closing_balance: number;
  closing_balance_base: number;
  overdue: number;
  overdue_base: number;
  not_due: number;
  unapplied_credits: number;
  transactions: CustomerStatementTransaction[];
  open_items: CustomerStatementOpenItem[];
  ageing: CustomerStatementAgeing;
  integrity_difference: number;
}

export interface CustomerStatement {
  statement_ref: string;
  generated_at: string;
  period: { from: string; to: string };
  base_currency: string;
  company: {
    name: string;
    address: string;
    phone: string;
    email: string;
    website: string;
    tax_registration_number: string;
    trade_license_number: string;
    bank: {
      bank_name: string;
      account_name: string;
      account_number: string;
      iban: string;
      swift_code: string;
      routing_code: string;
      bank_address: string;
    };
  };
  customer: {
    id: string;
    customer_code: string;
    customer_name: string;
    contact_person: string;
    email: string;
    phone: string;
    address_lines: string[];
    tax_registration_number: string;
    payment_terms: string;
    credit_days: number | null;
    credit_limit: number | null;
  };
  summary: {
    base_currency: string;
    closing_balance: number;
    overdue: number;
    open_invoices: number;
    unapplied_credits: number;
    credit_limit: number | null;
    credit_available: number | null;
    credit_days: number | null;
    payment_terms: string;
    last_payment: { date: string; receipt_number: string; amount: number; currency: string } | null;
    oldest_overdue_days: number;
    invoices_in_period: number;
    payments_in_period: number;
  };
  sections: CustomerStatementSection[];
  reconciliation: { gl_balance: number; statement_balance: number; difference: number; matched: boolean };
}

export interface CustomerStatementParams {
  customer_id: string;
  from_date: string;
  to_date: string;
  currency?: string;
  mode?: CustomerStatementMode;
}

export interface CustomerStatementDispatch {
  id: string;
  customer_id: string;
  statement_ref: string;
  period_from: string;
  period_to: string;
  mode: string;
  closing_balance: number;
  currency: string;
  recipients: string[];
  cc: string[];
  subject: string;
  status: 'Sent' | 'Failed';
  error_message: string | null;
  sent_by: string | null;
  sent_by_name: string | null;
  sent_at: string;
}

export interface SendCustomerStatementPayload extends CustomerStatementParams {
  mode: CustomerStatementMode;
  to: string[];
  cc?: string[];
  subject?: string;
  message?: string;
}

export async function getCustomerStatement(params: CustomerStatementParams): Promise<CustomerStatement> {
  const response = await api.get<{ success: boolean; data: CustomerStatement }>('/finance/reports/customer-statement', { params });
  const data = (response as any)?.data ?? response;
  return data?.data ?? data;
}

/** Server-rendered letterhead PDF (same file the customer receives by email). */
export async function getCustomerStatementPdf(params: CustomerStatementParams): Promise<Blob> {
  const res = await api.getClient().get('/finance/reports/customer-statement/pdf', { params, responseType: 'blob' });
  return res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'application/pdf' });
}

export async function sendCustomerStatement(payload: SendCustomerStatementPayload): Promise<CustomerStatementDispatch> {
  const response = await api.post<{ success: boolean; data: CustomerStatementDispatch }>(
    '/finance/reports/customer-statement/email',
    payload
  );
  const data = (response as any)?.data ?? response;
  return data?.data ?? data;
}

export async function getCustomerStatementDispatches(customerId: string): Promise<CustomerStatementDispatch[]> {
  const response = await api.get<{ success: boolean; data: CustomerStatementDispatch[] }>(
    '/finance/reports/customer-statement/dispatches',
    { params: { customer_id: customerId } }
  );
  const data = (response as any)?.data ?? response;
  return data?.data ?? data ?? [];
}

export async function getSupplierStatement(params: { supplier_id: string; from_date: string; to_date: string }): Promise<StatementReport> {
  const response = await api.get<{ success: boolean; data: StatementReport }>(
    '/finance/reports/supplier-statement',
    { params }
  );
  const data = (response as any)?.data ?? response;
  return data?.data ?? data;
}
